import { getStackType } from "@app/deploy";
import {
  PRINT_REPORT_FRAMEWORK_ORDER,
  type SecurityControlFrameworkMetrics,
} from "@app/deploy/security-controls";
import type { DeployStack } from "@app/types";

/** Print report brand and status colors (not yet in design tokens). */
export const REPORT_BRAND_PURPLE = "#4D1ED8";
export const REPORT_STATUS_RED = "#EF4444";
export const REPORT_STATUS_GREEN = "#00633F";
export const REPORT_FOOTER_PLUM = "#98256A";

export const formatPrintReportFooterDate = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** Cover-page timestamp: long report date and local time. */
export const formatPrintReportCoverGeneratedAt = (date: Date) => {
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

/** Comma-separated AWS regions for the given stacks. */
export const regionsForStacks = (stacks: DeployStack[]): string => {
  const regions = [...new Set(stacks.map((s) => s.region))].filter(Boolean);
  return regions.sort().join(", ") || "—";
};

export const formatStackCountLabel = (
  count: number,
  singular: string,
  plural: string,
): string => `${count} ${count === 1 ? singular : plural}`;

export const buildStackCountDisplay = (counts: {
  dedicated: number;
  shared: number;
  selfHosted: number;
}): string => {
  const parts: string[] = [];
  if (counts.dedicated > 0) {
    parts.push(
      formatStackCountLabel(
        counts.dedicated,
        "Dedicated Stack",
        "Dedicated Stacks",
      ),
    );
  }
  if (counts.shared > 0) {
    parts.push(
      formatStackCountLabel(counts.shared, "Shared Stack", "Shared Stacks"),
    );
  }
  if (counts.selfHosted > 0) {
    parts.push(
      formatStackCountLabel(
        counts.selfHosted,
        "Self-Hosted Stack",
        "Self-Hosted Stacks",
      ),
    );
  }
  return parts.join(", ") || "0 Stacks";
};

export const partitionScopedStacks = (
  stacks: DeployStack[],
  scopeStackIds: Set<string>,
) => {
  const stacksInScope = stacks.filter((s) => scopeStackIds.has(s.id));
  return {
    stacksInScope,
    dedicated: stacksInScope.filter((s) => getStackType(s) === "dedicated"),
    shared: stacksInScope.filter((s) => getStackType(s) === "shared"),
    selfHosted: stacksInScope.filter((s) => getStackType(s) === "self_hosted"),
  };
};

export const getIsolatedVpcsRegionStacks = (
  dedicated: DeployStack[],
  shared: DeployStack[],
  selfHosted: DeployStack[],
): DeployStack[] => {
  const dedicatedCount = dedicated.length;
  const sharedCount = shared.length;
  const selfHostedCount = selfHosted.length;
  if (dedicatedCount > 0 && sharedCount === 0 && selfHostedCount === 0) {
    return dedicated;
  }
  if (sharedCount > 0 && dedicatedCount === 0 && selfHostedCount === 0) {
    return shared;
  }
  if (selfHostedCount > 0 && dedicatedCount === 0 && sharedCount === 0) {
    return selfHosted;
  }
  return [...dedicated, ...shared, ...selfHosted];
};

export const getIsolatedVpcsDescriptionText = (
  dedicatedCount: number,
  sharedCount: number,
  selfHostedCount: number,
): string => {
  const onlyDedicated =
    dedicatedCount > 0 && sharedCount === 0 && selfHostedCount === 0;
  const onlyShared =
    sharedCount > 0 && dedicatedCount === 0 && selfHostedCount === 0;
  const onlySelfHosted =
    selfHostedCount > 0 && dedicatedCount === 0 && sharedCount === 0;

  if (onlyDedicated) {
    return "Dedicated infrastructure stacks providing tenant isolation.";
  }
  if (onlyShared) {
    return "Scoped environments run on shared Aptible infrastructure (multi-tenant).";
  }
  if (onlySelfHosted) {
    return "Self-hosted stacks running in your AWS account.";
  }

  const parts: string[] = [];
  if (dedicatedCount > 0) {
    parts.push("Dedicated stacks provide tenant isolation.");
  }
  if (sharedCount > 0) {
    parts.push("Shared stacks use multi-tenant Aptible infrastructure.");
  }
  if (selfHostedCount > 0) {
    parts.push("Self-hosted stacks run in your AWS account.");
  }
  return parts.join(" ");
};

export const hasPrintReportControlFilters = (
  categoryParams: string[],
  frameworkParams: string[],
): boolean =>
  categoryParams.length > 0 ||
  (frameworkParams.length > 0 && !frameworkParams.includes("all"));

export const buildPrintReportFilterLines = ({
  categoryParams,
  frameworkParams,
  frameworkMetrics,
}: {
  categoryParams: string[];
  frameworkParams: string[];
  frameworkMetrics: SecurityControlFrameworkMetrics;
}): string[] => {
  const lines: string[] = [];
  if (categoryParams.length > 0) {
    lines.push(`Categories: ${categoryParams.join(", ")}`);
  }
  const selectedFrameworks = frameworkParams.filter(
    (handle) => handle !== "all",
  );
  if (selectedFrameworks.length > 0) {
    const names = selectedFrameworks.map(
      (handle) => frameworkMetrics[handle]?.name ?? handle,
    );
    lines.push(`Frameworks: ${names.join(", ")}`);
  }
  return lines;
};

export const buildPrintReportComplianceOverviewRows = (
  metrics: SecurityControlFrameworkMetrics,
  frameworkParams: string[],
): { framework: string; percentage: number }[] => {
  const hasFrameworkFilter =
    frameworkParams.length > 0 && !frameworkParams.includes("all");
  const handles = hasFrameworkFilter
    ? frameworkParams
    : [...PRINT_REPORT_FRAMEWORK_ORDER];

  const rows: { framework: string; percentage: number }[] = [];
  for (const handle of handles) {
    const metric = metrics[handle];
    if (!metric) continue;
    if (hasFrameworkFilter && metric.controlCount === 0) continue;
    rows.push({
      framework: metric.name,
      percentage: metric.progressPercentage,
    });
  }
  return rows;
};

/** Pluralize resource type for display (e.g. "Database" → "Databases"). */
export const pluralizeResourceType = (
  resourceType: string,
  count: number,
): string => {
  const word = resourceType || "Resource";
  return count === 1 ? word : `${word}s`;
};

export const statusLabels: Record<string, string> = {
  COMPLETE: "Satisfied",
  PARTIAL: "Partially Satisfied",
  NOT_STARTED: "Not Satisfied",
  NO_SCOPE: "Not In-Scope",
};

/** Pill-style colors as containers for print status badges. */
export const reportStatusContainerClass: Record<string, string> = {
  COMPLETE:
    "report-status-badge bg-lime-100 border border-lime-300 text-forest",
  NOT_STARTED: "report-status-badge bg-red-100 border border-red-300 text-red",
  PARTIAL:
    "report-status-badge bg-indigo-100 border border-indigo-300 text-indigo",
  NO_SCOPE:
    "report-status-badge bg-gray-100 border border-gray-300 text-gray-600",
};
